import { CATALYST_WEAPON_IDS, combatSourceAtLevel, type CatalystWeaponId, type ReferenceWeaponLevel } from '../reference/weapon-progression-v0.js';
import type { CombatSourceDefinition } from './source-profile.js';
import type { WeaponContactProfileId } from './basic-attack-geometry.js';
import { pricedCastExpectedHits } from '../eval/expected-hits.js';
import { GENERATION_EVAL_CONTEXT_V1 } from '../generation/generation-eval-context.js';
import { detonationBlastApplication } from '../generation/detonation.js';
import { resolveLegacyDeliveryForApplication } from '../skill-engine/application.js';

/** All subtype curves use raw invested MAG. */
function requireRawMag(rawMag: number): number {
  if (!Number.isFinite(rawMag) || rawMag < 0) throw new Error('catalyst raw MAG must be finite and non-negative');
  return rawMag;
}

/** User-facing diameter: 0.5 m at MAG 0 and 3.5 m at MAG 500. No upper cap. */
export function blastCatalystRadiusM(rawMag: number): number {
  return 0.25 + 0.003 * requireRawMag(rawMag);
}

/** Basic-only MAG growth shared by each Catalyst shot damage and its mana cost. */
export function catalystBasicGrowthMultiplier(rawMag: number): number {
  return 1 + requireRawMag(rawMag) / 3000;
}

/** Direct/basic damage starts below Arcane and reaches parity at MAG 500. */
export function blastCatalystBasicDamageMultiplier(rawMag: number): number {
  return 0.90 + requireRawMag(rawMag) / 5000;
}

/** Basic-only weapon trait: 1.10 base cadence and linear raw-MAG growth. */
export function rapidCatalystRateMultiplier(rawMag: number): number {
  return 1.10 * (1 + requireRawMag(rawMag) / 1100);
}

/**
 * Rapid's basic is tuned on the reference damage divided by this and scaled back by the multiplier below.
 * Only `resolveBasicAttackSource` uses it, so the basic's floating-point result is bit-identical to before
 * the crafted-skill divisor was removed; skills no longer see it.
 */
export const RAPID_CATALYST_BASIC_SOURCE_DIVISOR = 1.06;
/** Applied to the divided basic source to realize 80% of Arcane. */
export const RAPID_CATALYST_BASIC_DAMAGE_MULTIPLIER = 0.80 * RAPID_CATALYST_BASIC_SOURCE_DIVISOR;

/** Blast: the share of a hit that lands on each neighbour, for the basic and for a crafted magic skill. */
export const BLAST_CATALYST_SPLASH_FRACTION = 0.32;
/** Rapid, crafted magic skills: recast interval, per-hit damage and mana per cast, so damage and mana per second match Arcane. */
export const RAPID_CATALYST_SKILL_FACTOR = 0.8;

/** What the held Catalyst kind does to a crafted magic skill, realized at cast and never priced. */
export interface CatalystSkillSplash {
  readonly fraction: number;
  readonly radiusM: number;
  /** Scales the primary hit so primary plus expected splash equals the unmodified expected total. */
  readonly primaryScale: number;
  /** Expected neighbours inside the radius per landed hit (the pricing encounter model). */
  readonly expectedNeighbours: number;
}

export function catalystSkillSplash(id: CatalystWeaponId | undefined, rawMag: number): CatalystSkillSplash | undefined {
  if (id !== 'BLAST_CATALYST') return undefined;
  const radiusM = blastCatalystRadiusM(rawMag);
  const ctx = GENERATION_EVAL_CONTEXT_V1;
  const application = detonationBlastApplication({ radiusM, timing: 'CONTACT', directDamage: 'MOVED' } as Parameters<typeof detonationBlastApplication>[0]);
  const lowered = resolveLegacyDeliveryForApplication(application);
  if (!lowered.ok) throw new Error('blast splash delivery does not lower');
  const hits = pricedCastExpectedHits([{ stageId: 'splash', delivery: lowered.delivery, application, units: [[{ x: 0, y: 0, directionRad: 0 }]] }], ctx);
  if (!hits.ok) throw new Error('blast splash has no expected-hits model: ' + hits.message);
  // The blast circle holds the guaranteed primary plus its neighbours.
  const expectedNeighbours = hits.byStageId.get('splash')!.expectedHits - 1;
  return { fraction: BLAST_CATALYST_SPLASH_FRACTION, radiusM, expectedNeighbours, primaryScale: 1 / (1 + BLAST_CATALYST_SPLASH_FRACTION * expectedNeighbours) };
}

/** Rapid's crafted magic skills cast this much more often, for this much less damage and mana each; 1 for every other kind. */
export function catalystSkillRapidFactor(id: CatalystWeaponId | undefined): number {
  return id === 'RAPID_CATALYST' ? RAPID_CATALYST_SKILL_FACTOR : 1;
}

export function catalystSourceAtLevel(id: CatalystWeaponId, level: ReferenceWeaponLevel): CombatSourceDefinition {
  if (!(CATALYST_WEAPON_IDS as readonly string[]).includes(id)) throw new Error('unknown Catalyst weapon: ' + id);
  const reference = combatSourceAtLevel('CATALYST_REFERENCE', level);
  return Object.freeze({
    id: 'playable.' + id.toLowerCase() + '.lv' + level,
    family: 'CATALYST' as const,
    weaponId: id,
    baseDamage: reference.baseDamage,
    baseAttackRate: reference.baseAttackRate,
  });
}

/**
 * The source a weapon-contact profile's wielder holds at a reference level: any
 * catalyst for `CATALYST` (the three share one contact shape; Arcane is the
 * neutral one), else the balance weapon of that id.
 */
export function contactProfileSourceAtLevel(profileId: WeaponContactProfileId, level: ReferenceWeaponLevel): CombatSourceDefinition {
  return profileId === 'CATALYST' ? catalystSourceAtLevel('ARCANE_CATALYST', level) : combatSourceAtLevel(profileId, level);
}
